/**
 * The character sheet, as a full-screen React island rendered from `@annwn/ui`
 * (RC2-03) — the milestone's flagship library surface.
 *
 * A framed, tabbed sheet the player opens to read who they are becoming:
 * *Character* (identity, attributes, the familiarity ladder), *Equipment* (the
 * paper-doll and a categorised inventory), a *Journal* of the errands they carry,
 * and a *Chronicle*. Every visible part is a library component — `Tabs`,
 * `BodyDiagram`, `Meter`, `Table`, `WoundLedger`, `Badge` — composed here, never
 * forked; this file adds view wiring and holds no game state beyond which tab is
 * open. It receives already-derived view models (`characterSheetView`) and, like
 * the other islands, never touches a socket or the simulation.
 *
 * The one bit of memory it keeps is the once-only glow (AC-3): a `RungMemory`
 * remembers which familiarity rungs the player has already been shown crossing,
 * so a freshly-crossed rung glows awen for the viewing it was earned in and is
 * quiet ever after. It commits on close.
 */

import { useState, type DragEvent } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import type { SelfSnapshot } from '@annwn/protocol';
import {
  Badge,
  BodyDiagram,
  Button,
  Meter,
  Table,
  Tabs,
  WoundLedger,
  type Tab,
  type TableColumn,
} from '@annwn/ui';

import {
  characterSheetView,
  freshnessLabel,
  RungMemory,
  type CharacterSheetView,
  type CraftVerb,
  type RungStore,
} from './character-sheet-view-model.js';
import { type SatchelLine } from './satchel-pane.js';

/** The design-spec §7 ceiling — attributes run 1..20, and the sheet shows the value. */
const ATTRIBUTE_MAX = 20;

export interface CharacterSheetOptions {
  /** The player asked to close the sheet — the game folds it shut. */
  onClose(): void;
  /** Equip the authoritative carried stack dropped or clicked onto the body. */
  onEquip(stackKey: string): void;
  /** Put the weapon currently in hand back into the inventory. */
  onStow(): void;
  /** Perform one server-offered making verb at the player's current place. */
  onCraft(verb: CraftVerb): void;
  /** Where the once-only glow is remembered. `localStorage`, or a test's stub. */
  store?: RungStore | null;
}

const EQUIPMENT_DRAG_TYPE = 'application/x-annwn-equipment-stack';

function inventoryColumns(
  onEquip: (stackKey: string) => void,
): readonly TableColumn<SatchelLine>[] {
  return [
    {
      key: 'item',
      header: 'Item',
      cell: (line) => {
        const note = freshnessLabel(line.freshness);
        const label = (
          <span className={line.spent ? 'sheet-cell sheet-cell--spent' : 'sheet-cell'}>
            {line.label}
            {note ? <span className="sheet-cell__freshness"> — {note}</span> : null}
          </span>
        );
        if (!line.equippable) return label;
        return (
          <button
            className="sheet-equippable"
            draggable
            onClick={() => onEquip(line.key)}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData(EQUIPMENT_DRAG_TYPE, line.key);
            }}
            title="Drag to the body, or click to take in hand"
            type="button"
          >
            {label}
            <span className="sheet-equippable__hint">equip</span>
          </button>
        );
      },
    },
    {
      key: 'weight',
      header: 'Weight',
      cell: (line) => line.weight,
      align: 'end',
      width: '6rem',
    },
  ];
}

function CharacterTab({ view }: { view: CharacterSheetView }) {
  return (
    <div className="sheet-character">
      <header className="sheet-ident">
        <h3 className="sheet-ident__name">{view.identity.name}</h3>
        {view.identity.trade || view.identity.blurb ? (
          <p className="sheet-ident__line">
            {view.identity.trade ? <b>{view.identity.trade}</b> : null}
            {view.identity.trade && view.identity.blurb ? ' — ' : null}
            {view.identity.blurb}
          </p>
        ) : null}
      </header>

      <p className="sheet-section-cap">Attributes</p>
      <div className="sheet-attrs">
        {view.attributes.map((attr) => (
          <Meter
            key={attr.key}
            label={attr.label}
            value={attr.value}
            max={ATTRIBUTE_MAX}
            displayValue={attr.value}
            variant="stamina"
          />
        ))}
      </div>

      <p className="sheet-section-cap">Familiarities</p>
      <div className="sheet-fam">
        {view.familiarities.map((rung) => (
          <Meter
            key={rung.familiarity}
            className={[
              'sheet-fam__row',
              rung.fresh ? 'sheet-fam__row--fresh' : '',
              rung.locked ? 'sheet-fam__row--locked' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            label={rung.label}
            value={Math.round(rung.fill * 100)}
            max={100}
            displayValue={rung.phrase}
            variant={rung.locked ? 'danger' : 'life'}
          />
        ))}
      </div>
    </div>
  );
}

function EquipmentTab({
  view,
  onEquip,
  onStow,
}: {
  view: CharacterSheetView;
  onEquip(stackKey: string): void;
  onStow(): void;
}) {
  const [overBody, setOverBody] = useState(false);
  const receive = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setOverBody(false);
    const stackKey = event.dataTransfer.getData(EQUIPMENT_DRAG_TYPE);
    if (stackKey) onEquip(stackKey);
  };

  return (
    <div className="sheet-equipment">
      <div
        aria-label="Equipped on your body"
        className={overBody ? 'sheet-doll sheet-doll--drop' : 'sheet-doll'}
        onDragEnter={(event) => {
          event.preventDefault();
          setOverBody(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOverBody(false);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        }}
        onDrop={receive}
      >
        <p className="sheet-section-cap">Body</p>
        <BodyDiagram parts={view.parts} />
        <p className="sheet-doll__caption">{view.figureCaption}</p>
        <div aria-live="polite" className="sheet-equipped">
          <span>
            {view.heldWeapon
              ? `Right hand — ${view.heldWeapon}${
                  view.heldAmmunition === null
                    ? ''
                    : ` · ${view.heldAmmunition} ${view.heldAmmunition === 1 ? 'arrow' : 'arrows'}`
                }`
              : 'Hands empty'}
          </span>
          {view.heldWeaponStowable ? (
            <Button onClick={onStow} type="button" variant="ghost">
              Put away
            </Button>
          ) : null}
        </div>
        <p className="sheet-doll__drop-hint">Drag a weapon here, or click it in your pack.</p>
        <WoundLedger wounds={view.wounds} label="Wounds" emptyLabel="Unhurt." />
      </div>
      <div className="sheet-inventory">
        <p className="sheet-section-cap">
          Carried
          {view.inventoryWeight ? (
            <span className="sheet-inventory__weight"> · {view.inventoryWeight}</span>
          ) : null}
        </p>
        {view.inventory.length === 0 ? (
          <p className="sheet-inventory__empty">
            {view.inventoryEmpty ?? 'You are carrying nothing.'}
          </p>
        ) : (
          view.inventory.map((group) => (
            <Table
              key={group.category}
              className="sheet-inventory__table"
              columns={inventoryColumns(onEquip)}
              rows={group.lines}
              rowKey={(line) => line.key}
              caption={group.category}
            />
          ))
        )}
      </div>
    </div>
  );
}

function CraftingTab({
  view,
  onCraft,
}: {
  view: CharacterSheetView;
  onCraft(verb: CraftVerb): void;
}) {
  return (
    <div className="sheet-crafting">
      <header>
        <p className="sheet-section-cap">Making in Annwn</p>
        <p className="sheet-crafting__intro">
          Making is place-based. Carry the ingredients, stand at a fire, then choose the work. The
          buttons below always use the same live rules as the world.
        </p>
      </header>
      <div className="sheet-crafting__actions">
        {view.crafting.actions.map((action) => (
          <div className="sheet-crafting__action" key={action.verb}>
            <Button
              disabled={!action.available}
              onClick={() => onCraft(action.verb)}
              type="button"
              variant={action.available ? 'primary' : 'ghost'}
            >
              {action.label}
            </Button>
            {action.reason ? <span>{action.reason}</span> : null}
          </div>
        ))}
      </div>
      <p className="sheet-section-cap">Recipes</p>
      <ul className="sheet-recipes">
        {view.crafting.recipes.map((recipe) => (
          <li
            className={recipe.ready ? 'sheet-recipe sheet-recipe--ready' : 'sheet-recipe'}
            key={recipe.id}
          >
            <span className="sheet-recipe__mark" aria-hidden="true">
              {recipe.ready ? '◆' : '◇'}
            </span>
            <span>
              <b>{recipe.result}</b>
              <small>
                {recipe.ingredients.join(' + ')} ·{' '}
                {recipe.kind === 'cook' ? 'cook at a fire' : 'make up at a fire'}
              </small>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JournalTab({ view }: { view: CharacterSheetView }) {
  if (view.errands.length === 0) {
    return <p className="sheet-journal__empty">No errands in hand. Ask in the glen.</p>;
  }
  return (
    <ul className="sheet-journal">
      {view.errands.map((errand) => (
        <li key={errand.id} className="sheet-journal__errand">
          <p className="sheet-journal__prompt">{errand.prompt}</p>
          {errand.objectives.length > 0 ? (
            <ul className="sheet-journal__objectives">
              {errand.objectives.map((objective) => (
                <li key={objective.id}>
                  {objective.current >= objective.count ? '✓' : '·'} {objective.text}
                  {objective.count > 1 ? ` (${objective.current}/${objective.count})` : ''}
                </li>
              ))}
            </ul>
          ) : null}
          {errand.giver ? (
            <p className="sheet-journal__giver">
              <Badge tone="myth">{errand.giver}</Badge>
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ChronicleTab() {
  // As room allows (RC2-03): the running chronicle lives at the hearth in the
  // log rail; the sheet points to it rather than mirroring it here.
  return (
    <p className="sheet-chronicle__note">Your deeds are told in the chronicle, at the hearth.</p>
  );
}

function CharacterSheet({
  view,
  onEquip,
  onStow,
  onCraft,
}: {
  view: CharacterSheetView;
  onEquip(stackKey: string): void;
  onStow(): void;
  onCraft(verb: CraftVerb): void;
}) {
  const tabs: readonly Tab[] = [
    { id: 'character', label: 'Character', panel: <CharacterTab view={view} /> },
    {
      id: 'equipment',
      label: 'Equipment',
      panel: <EquipmentTab onEquip={onEquip} onStow={onStow} view={view} />,
    },
    { id: 'crafting', label: 'Making', panel: <CraftingTab onCraft={onCraft} view={view} /> },
    { id: 'journal', label: 'Journal', panel: <JournalTab view={view} /> },
    { id: 'chronicle', label: 'Chronicle', panel: <ChronicleTab /> },
  ];
  return (
    <div className="sheet-overlay">
      <Tabs defaultTab="equipment" tabs={tabs} label="Character sheet" />
      <p className="sheet-hint">p or Esc to close.</p>
    </div>
  );
}

export interface CharacterSheetHandle {
  update(self: SelfSnapshot | null, tick: number): void;
  setOpen(open: boolean): void;
  destroy(): void;
}

/**
 * Mount the sheet island and return the imperative handle `main.ts` drives.
 *
 * Like the satchel window, visibility lives here: closed, the island renders
 * nothing. Closing commits the once-only glow — every rung shown this viewing is
 * remembered, so nothing glows twice — which is why the sheet must be told when
 * it shuts, not merely hidden.
 */
export function mountCharacterSheet(
  container: HTMLElement,
  options: CharacterSheetOptions,
): CharacterSheetHandle {
  const root: Root = createRoot(container);
  const memory = new RungMemory(options.store ?? null);
  let self: SelfSnapshot | null = null;
  let tick = 0;
  let open = false;
  let shown: CharacterSheetView | null = null;

  const render = (): void => {
    if (open && self) {
      shown = characterSheetView(self, tick, memory);
      root.render(
        <CharacterSheet
          onCraft={options.onCraft}
          onEquip={options.onEquip}
          onStow={options.onStow}
          view={shown}
        />,
      );
    } else {
      root.render(null);
    }
  };

  const commit = (): void => {
    if (shown) memory.remember(shown.identity.name, shown.rungs);
  };

  render();

  return {
    update(nextSelf, nextTick) {
      self = nextSelf;
      tick = nextTick;
      render();
    },
    setOpen(next) {
      if (open === next) return;
      // Commit as it closes, so a rung crossed this viewing never glows again.
      if (!next) commit();
      open = next;
      render();
    },
    destroy() {
      commit();
      root.unmount();
    },
  };
}
